import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';
import { FormikValues } from 'formik';

import api from '../../../utils/api';
import { GET_ADMIN_SCHOLARSHIP_CONFIG_QUERY_KEY } from './useGetAdminScholarshipConfigQuery';

interface UploadScholarshipTemplateResponse {
  statusCode?: number;
  code?: number;
  message: string;
  data?: unknown;
}

const getFileFromValue = (value: unknown) => {
  if (value instanceof File) {
    return value;
  }

  if (value instanceof FileList) {
    return value[0];
  }

  return undefined;
};

export const useUploadScholarshipTemplateMutation = (handleClose?: () => void) => {
  const queryClient = useQueryClient();

  const uploadScholarshipTemplate = async (payload: FormData): Promise<UploadScholarshipTemplateResponse> => {
    return (await api.post('admin/scholarship/template/upload', payload))?.data;
  };

  const mutation = useMutation({
    mutationKey: ['upload-scholarship-template'],
    mutationFn: uploadScholarshipTemplate,
  });

  const uploadScholarshipTemplateHandler = async (values: FormikValues | File, resetForm?: () => void) => {
    const file = values instanceof File ? values : getFileFromValue(values?.file);

    if (!file) {
      notification.error({
        message: 'Error',
        description: 'Please select a scholarship template to upload',
      });
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      await mutation.mutateAsync(formData, {
        onSuccess: (data) => {
          notification.success({
            message: data?.message || 'Scholarship template uploaded successfully',
          });

          queryClient.refetchQueries({
            queryKey: GET_ADMIN_SCHOLARSHIP_CONFIG_QUERY_KEY,
          });

          resetForm?.();
          handleClose?.();
        },
      });
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description:
          error?.response?.data?.message ||
          error?.response?.data?.title ||
          'Failed to upload scholarship template',
      });
    }
  };

  return {
    uploadScholarshipTemplateHandler,
    isUploadScholarshipTemplateLoading: mutation.isPending,
    isLoading: mutation.isPending,
  };
};
